import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { 
  Users, Pause, Play, X, Plus, Trash2, Key, Info, TrendingUp, Award,
  BookOpen, MoreHorizontal, Edit2, Check, X as XIcon, CreditCard, ExternalLink, FileText,
  Loader2, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightSmall,
  MessageSquare, Link2, ArrowUpDown, ArrowUp, ArrowDown, Tag, FlaskConical
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { getDisplayName } from "@/lib/userDisplay";
import { formatJamaicaDate as formatDate } from "@/lib/datetime";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

type User = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  school?: string;
  role: string;
  grade?: number;
  subscriptionStatus?: string;
  subscriptionEndDate?: string;
  subscribedGrade?: number;
  subscriptionPlan?: string;
  isTestAccount?: boolean;
  createdAt: string;
  lastActiveAt?: string;
  parentFirstName?: string;
  parentLastName?: string;
  parentEmail?: string;
  parentPhone?: string;
};

type PaginatedUsersResponse = {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};


// ─── helpers ────────────────────────────────────────────────────────────────

function formatPhoneDisplay(phone?: string | null): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  // Strip leading country code: +1876... or 1876... → 876...
  const local = digits.startsWith("1876") && digits.length === 11 ? digits.slice(1) : digits;
  // Format as (876) XXX-XXXX if 10 digits
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return phone;
}

function getLastActiveDisplay(lastActiveAt?: string | null): { color: string; text: string; pulse: boolean } {
  if (!lastActiveAt) return { color: "bg-muted-foreground/40", text: "Never", pulse: false };
  const now = new Date();
  const last = new Date(lastActiveAt);
  const diffMs = now.getTime() - last.getTime();
  const diffMins = diffMs / (1000 * 60);
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffMins <= 5) return { color: "bg-green-500", text: "Online", pulse: true };
  if (diffHours <= 3) return { color: "bg-green-400", text: `${Math.floor(diffHours)}h ago`, pulse: false };
  if (diffHours <= 24) return { color: "bg-yellow-400", text: "Today", pulse: false };
  if (diffDays <= 2) return { color: "bg-yellow-400", text: "Yesterday", pulse: false };
  return { color: "bg-muted-foreground/40", text: `${Math.floor(diffDays)}d ago`, pulse: false };
}

function LastActiveDot({ lastActiveAt }: { lastActiveAt?: string | null }) {
  const { color, text, pulse } = getLastActiveDisplay(lastActiveAt);
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${color} ${pulse ? "animate-pulse" : ""}`} />
      <span className="text-sm text-muted-foreground whitespace-nowrap">{text}</span>
    </div>
  );
}

function SortIcon({ field, sortBy, sortDir }: { field: string; sortBy: string; sortDir: 'asc' | 'desc' }) {
  if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  return sortDir === 'asc'
    ? <ArrowUp className="w-3 h-3 ml-1 text-primary" />
    : <ArrowDown className="w-3 h-3 ml-1 text-primary" />;
}

// ─── animated children rows (always rendered, fetch on expand) ────────────────

function ChildrenRows({
  parentId,
  isExpanded,
  onViewDetails,
}: {
  parentId: string;
  isExpanded: boolean;
  onViewDetails: (id: string) => void;
}) {
  const { data: children, isLoading } = useQuery<User[]>({
    queryKey: [`/api/admin/users/${parentId}/children`],
    enabled: isExpanded,
  });

  return (
    <tr>
      <td colSpan={8} className="p-0 border-0">
        <div
          className={`overflow-hidden transition-all duration-200 ease-in-out ${
            isExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          {isLoading ? (
            <div className="py-3 pl-16 flex items-center gap-2 text-sm text-muted-foreground bg-muted/20">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading children…
            </div>
          ) : !children || children.length === 0 ? (
            <div className="py-3 pl-16 text-sm text-muted-foreground italic bg-muted/20">No children linked to this account</div>
          ) : (
            <table className="w-full">
              <tbody>
                {children.map((child) => (
                  <tr key={child.id} className="border-b bg-muted/20 last:border-b-0">
                    {/* indent + arrow */}
                    <td className="py-2 w-6">
                      <span className="text-muted-foreground/60 text-xs pl-2">↳</span>
                    </td>
                    {/* name */}
                    <td className="py-2 px-3">
                      <button
                        onClick={() => onViewDetails(child.id)}
                        className="font-medium text-primary hover:underline text-left text-sm leading-tight"
                      >
                        {child.firstName} {child.lastName}
                      </button>
                      <div className="text-xs text-muted-foreground">{getDisplayName(child)}</div>
                    </td>
                    {/* email */}
                    <td className="py-2 px-3 text-sm text-muted-foreground">{child.email || "–"}</td>
                    {/* grade */}
                    <td className="py-2 px-3 text-sm text-muted-foreground">
                      {child.grade ?? "–"}
                    </td>
                    {/* status */}
                    <td className="py-2 px-3">
                      {child.subscriptionStatus === "active"
                        ? <Badge className="bg-green-600 text-xs">Active</Badge>
                        : child.subscriptionStatus
                          ? <Badge variant="secondary" className="text-xs capitalize">{child.subscriptionStatus}</Badge>
                          : <Badge variant="outline" className="text-xs">No Sub</Badge>
                      }
                    </td>
                    {/* last active */}
                    <td className="py-2 px-3">
                      <LastActiveDot lastActiveAt={child.lastActiveAt} />
                    </td>
                    {/* expires placeholder */}
                    <td className="py-2 px-3" />
                    {/* actions */}
                    <td className="py-2 px-3 text-right">
                      <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => onViewDetails(child.id)}>
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  );
}


// ─── main component ──────────────────────────────────────────────────────────

export default function AdminUsersManagement() {
  const { toast } = useToast();

  // Single-user dialogs
  const [extendDays, setExtendDays] = useState(30);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [passwordResetUserId, setPasswordResetUserId] = useState<string | null>(null);
  const [applyDiscountUserId, setApplyDiscountUserId] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [userDetailsId, setUserDetailsId] = useState<string | null>(() => {
    // Auto-open detail panel when ?openUser=<id> is in the URL
    const params = new URLSearchParams(window.location.search);
    return params.get("openUser") || null;
  });
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [editedEmail, setEditedEmail] = useState("");
  const [editedUsername, setEditedUsername] = useState("");
  const [editingParentContact, setEditingParentContact] = useState(false);
  const [editedParentFirstName, setEditedParentFirstName] = useState("");
  const [editedParentLastName, setEditedParentLastName] = useState("");
  const [editedParentEmail, setEditedParentEmail] = useState("");
  const [editedParentPhone, setEditedParentPhone] = useState("");

  // Search / pagination / filters
  // /admin/users?search=… deep-links from the Subscriptions console straight to a user.
  const [searchTerm, setSearchTerm] = useState(() => { try { return new URLSearchParams(window.location.search).get("search") || ""; } catch { return ""; } });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  // Sort
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>("desc");

  // Parent-child expand
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Delete confirmation
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  // WhatsApp
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappTargetId, setWhatsappTargetId] = useState<string | null>(null);

  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, statusFilter, gradeFilter, roleFilter, sortBy, sortDir]);

  const handleCloseUserDetails = () => {
    setUserDetailsId(null);
    setEditingEmail(false);
    setEditingUsername(false);
    setEditedEmail("");
    setEditedUsername("");
    setEditingParentContact(false);
    setEditedParentFirstName("");
    setEditedParentLastName("");
    setEditedParentEmail("");
    setEditedParentPhone("");
  };

  const buildUsersUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    params.set("page", currentPage.toString());
    params.set("pageSize", pageSize.toString());
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (gradeFilter !== "all") params.set("grade", gradeFilter);
    if (roleFilter !== "all") params.set("role", roleFilter);
    params.set("excludeLinkedChildren", "true");
    params.set("sortBy", sortBy);
    params.set("sortDir", sortDir);
    return `/api/admin/users?${params.toString()}`;
  }, [debouncedSearch, currentPage, pageSize, statusFilter, gradeFilter, roleFilter, sortBy, sortDir]);

  const { data: usersData, isLoading, isFetching, isError, error } = useQuery<PaginatedUsersResponse>({
    queryKey: [buildUsersUrl()],
    placeholderData: keepPreviousData,
  });


  const { data: userDetails } = useQuery<{
    id: string; username: string; firstName: string; lastName: string; email?: string; school?: string;
    grade?: number; gender?: string; dateOfBirth?: string; role: string; subscriptionStatus?: string;
    subscriptionEndDate?: string; subscribedGrade?: number; subscriptionPlan?: string;
    parentName?: string; parentEmail?: string; parentPhone?: string; parentFirstName?: string; parentLastName?: string;
    lastActiveAt?: string;
    createdAt?: string;
    level?: { currentLevel?: number; totalXP?: number; levelXP?: number; nextLevelXP?: number };
    stats?: { totalTests: number; overallAccuracy: number; currentStreak: number; totalPoints?: number; totalCorrect?: number; subjectBreakdown?: Array<{ subject: string; points: number }> };
    referralInfo?: { referralCode: string; programType: string; totalReferrals: number; activeReferrals: number } | null;
  }>({
    queryKey: [`/api/admin/users/${userDetailsId}/details`],
    enabled: !!userDetailsId,
  });

  const { data: paymentHistory, isLoading: paymentHistoryLoading } = useQuery<{
    message?: string;
    upcomingPayments?: Array<{ amount: number; date: string; description: string; currency?: string }>;
    pastPayments?: Array<{ id: string; amount: number; date: string; status: string; description: string; currency?: string; invoiceUrl?: string; invoicePdf?: string }>;
  }>({
    queryKey: [`/api/admin/users/${userDetailsId}/payment-history`],
    enabled: !!userDetailsId,
  });

  // Only invalidate the paginated list: not children/details/payment-history queries
  const invalidateUsers = () => queryClient.invalidateQueries({
    predicate: q => typeof q.queryKey[0] === 'string' && /^\/api\/admin\/users\?/.test(q.queryKey[0] as string)
  });

  // ── single-user mutations ──────────────────────────────────────────────────
  const extendMutation = useMutation({
    mutationFn: ({ userId, days }: { userId: string; days: number }) =>
      apiRequest("POST", `/api/admin/users/${userId}/extend-subscription`, { days }),
    onSuccess: () => { invalidateUsers(); toast({ title: "Subscription extended" }); setSelectedUserId(null); },
    onError: () => toast({ title: "Extension failed", variant: "destructive" }),
  });

  const pauseMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/pause-subscription`, {}),
    onSuccess: () => { invalidateUsers(); toast({ title: "Subscription paused" }); },
    onError: () => toast({ title: "Pause failed", variant: "destructive" }),
  });

  const resumeMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/resume-subscription`, {}),
    onSuccess: () => { invalidateUsers(); toast({ title: "Subscription resumed" }); },
    onError: () => toast({ title: "Resume failed", variant: "destructive" }),
  });

  const grantTestAccessMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/grant-test-access`, {}),
    onSuccess: () => { invalidateUsers(); toast({ title: "Test access granted", description: "Account marked as test, no payment required." }); },
    onError: () => toast({ title: "Failed to grant test access", variant: "destructive" }),
  });

  const revokeTestAccessMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/revoke-test-access`, {}),
    onSuccess: () => { invalidateUsers(); toast({ title: "Test account deleted", description: "The test account has been permanently removed." }); },
    onError: () => toast({ title: "Failed to delete test account", variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("POST", `/api/admin/users/${userId}/cancel-subscription`, {}),
    onSuccess: () => {
      invalidateUsers();
      queryClient.invalidateQueries({ queryKey: ["/api/leaderboard"] });
      toast({ title: "Subscription canceled" });
    },
    onError: () => toast({ title: "Cancel failed", variant: "destructive" }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      apiRequest("POST", `/api/admin/users/${userId}/reset-password`, { newPassword: password }),
    onSuccess: () => { toast({ title: "Password reset" }); setPasswordResetUserId(null); setNewPassword(""); },
    onError: (e: any) => toast({ title: "Reset failed", description: e.message, variant: "destructive" }),
  });

  const updateGradeMutation = useMutation({
    mutationFn: ({ userId, grade }: { userId: string; grade: number }) =>
      apiRequest("PUT", `/api/admin/users/${userId}/grade`, { grade }),
    onSuccess: async (_d, v) => {
      invalidateUsers();
      await queryClient.refetchQueries({ queryKey: [`/api/admin/users/${v.userId}/details`] });
      toast({ title: "Grade updated" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const updateEmailMutation = useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) =>
      apiRequest("PUT", `/api/admin/users/${userId}/email`, { email }),
    onSuccess: async (_d, v) => {
      invalidateUsers();
      await queryClient.refetchQueries({ queryKey: [`/api/admin/users/${v.userId}/details`] });
      setEditingEmail(false);
      toast({ title: "Email updated" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const updateUsernameMutation = useMutation({
    mutationFn: ({ userId, username }: { userId: string; username: string }) =>
      apiRequest("PUT", `/api/admin/users/${userId}/username`, { username }),
    onSuccess: async (_d, v) => {
      invalidateUsers();
      await queryClient.refetchQueries({ queryKey: [`/api/admin/users/${v.userId}/details`] });
      setEditingUsername(false);
      toast({ title: "Username updated" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const updateParentContactMutation = useMutation({
    mutationFn: ({ userId, ...data }: { userId: string; parentFirstName: string; parentLastName: string; parentEmail: string; parentPhone: string }) =>
      apiRequest("PATCH", `/api/admin/users/${userId}/parent-contact`, data),
    onSuccess: async (_d, v) => {
      await queryClient.refetchQueries({ queryKey: [`/api/admin/users/${v.userId}/details`] });
      setEditingParentContact(false);
      toast({ title: "Parent contact updated" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => apiRequest("DELETE", `/api/admin/users/${userId}`, {}),
    onSuccess: () => {
      setDeleteUserId(null);
      invalidateUsers();
      queryClient.invalidateQueries({ queryKey: ["/api/leaderboard"] });
      toast({ title: "User deleted" });
    },
    onError: (e: any) => toast({ title: "Delete failed", description: e.message, variant: "destructive" }),
  });


  const whatsappMutation = useMutation({
    mutationFn: ({ userId, message }: { userId: string; message: string }) =>
      apiRequest("POST", `/api/admin/users/${userId}/whatsapp-parent`, { message }),
    onSuccess: () => {
      toast({ title: "Message sent" });
      setWhatsappOpen(false); setWhatsappMessage(""); setWhatsappTargetId(null);
    },
    onError: (e: any) => toast({ title: "Send failed", description: e.message, variant: "destructive" }),
  });

  const applyDiscountMutation = useMutation({
    mutationFn: ({ userId, code }: { userId: string; code: string }) =>
      apiRequest("POST", `/api/admin/users/${userId}/apply-coupon`, { code }),
    onSuccess: (data: any) => {
      toast({ title: "Discount applied", description: `${data.discountDescription} applied to this subscription.` });
      setApplyDiscountUserId(null);
      setCouponCode("");
    },
    onError: (e: any) => toast({ title: "Failed to apply discount", description: e.message, variant: "destructive" }),
  });

  // ── helpers ────────────────────────────────────────────────────────────────
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case "active": return <Badge className="bg-green-600">Active</Badge>;
      case "trial": return <Badge className="bg-blue-600">Trial</Badge>;
      case "past_due": return <Badge className="bg-orange-600">Past Due</Badge>;
      case "paused": return <Badge className="bg-yellow-600">Paused</Badge>;
      case "canceled":
      case "cancelled": return <Badge variant="destructive">Canceled</Badge>;
      case "expired": return <Badge variant="secondary">Expired</Badge>;
      case "inactive": return <Badge variant="secondary">Inactive</Badge>;
      default: return <Badge variant="outline">No Subscription</Badge>;
    }
  };

  const getTimeRemaining = (endDate?: string, status?: string) => {
    if (!endDate) return "N/A";
    const end = new Date(endDate);
    if (status === "canceled") return "Canceled";
    if (end < new Date()) return "Expired";
    return formatDistanceToNow(end, { addSuffix: true });
  };

  const toggleExpanded = (id: string) => setExpandedRows(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (isLoading && !usersData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto max-w-7xl px-4 py-8">
        <Card><CardContent className="py-8 text-center">
          <p className="text-destructive font-semibold">Failed to load users</p>
          <p className="text-muted-foreground mt-2">{error instanceof Error ? error.message : "An error occurred"}</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">User Management</h1>
          <p className="text-muted-foreground mt-2">View and manage all registered users and their subscriptions</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Registered Users ({usersData?.total || 0})
                  </CardTitle>
                  <CardDescription>Manage user subscriptions and account status</CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="input-search-users"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-44" data-testid="select-status-filter">
                    <SelectValue placeholder="Subscription Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="none">No Subscription</SelectItem>
                    <SelectItem value="canceled">Cancelled</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={gradeFilter} onValueChange={setGradeFilter}>
                  <SelectTrigger className="w-36" data-testid="select-grade-filter">
                    <SelectValue placeholder="Grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    <SelectItem value="4">Grade 4</SelectItem>
                    <SelectItem value="5">Grade 5</SelectItem>
                    <SelectItem value="6">Grade 6</SelectItem>
                    <SelectItem value="11">Grade 11</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-32" data-testid="select-role-filter">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="student">Child</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>

                {(statusFilter !== "all" || gradeFilter !== "all" || roleFilter !== "all") && (
                  <Button variant="ghost" size="sm" onClick={() => { setStatusFilter("all"); setGradeFilter("all"); setRoleFilter("all"); }}>
                    <XIcon className="w-4 h-4 mr-1" />Clear
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="relative">
              {isFetching && (
                <div className="absolute inset-0 bg-background/60 z-10 flex items-center justify-center rounded pointer-events-none">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px]">
                  <thead>
                    <tr className="border-b">
                      {/* chevron col */}
                      <th className="py-3 px-3 w-6" />
                      <th className="text-left py-3 px-3 font-semibold text-sm">User</th>
                      <th className="text-left py-3 px-3 font-semibold text-sm">Email</th>
                      <th className="text-left py-3 px-3 font-semibold text-sm">Grade</th>
                      <th className="text-left py-3 px-3 font-semibold text-sm">Status</th>
                      {/* sortable: last active */}
                      <th className="text-left py-3 px-3 font-semibold text-sm">
                        <button className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort("lastActiveAt")}>
                          Last Active <SortIcon field="lastActiveAt" sortBy={sortBy} sortDir={sortDir} />
                        </button>
                      </th>
                      <th className="text-left py-3 px-3 font-semibold text-sm">
                        <button className="flex items-center hover:text-primary transition-colors" onClick={() => handleSort("createdAt")}>
                          Joined <SortIcon field="createdAt" sortBy={sortBy} sortDir={sortDir} />
                        </button>
                      </th>
                      <th className="text-left py-3 px-3 font-semibold text-sm">Expires</th>
                      <th className="text-right py-3 px-3 font-semibold text-sm">Actions</th>
                    </tr>
                  </thead>
                  {usersData?.users?.map((user) => {
                      const isParent = user.role === "parent";
                      const isExpanded = expandedRows.has(user.id);

                      return (
                        <tbody key={user.id}>
                          <tr className="border-b hover-elevate" data-testid={`row-user-${user.id}`}>
                            <td className="py-3 px-3">
                              {isParent && (
                                <button
                                  onClick={() => toggleExpanded(user.id)}
                                  className="text-muted-foreground hover:text-primary transition-colors"
                                  data-testid={`button-expand-${user.id}`}
                                >
                                  {isExpanded
                                    ? <ChevronDown className="w-4 h-4" />
                                    : <ChevronRightSmall className="w-4 h-4" />}
                                </button>
                              )}
                            </td>
                            <td className="py-3 px-3">
                              <button onClick={() => setUserDetailsId(user.id)} className="font-medium text-primary hover:underline text-left" data-testid={`button-user-name-${user.id}`}>
                                {user.firstName} {user.lastName}
                              </button>
                              <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                                {getDisplayName(user)}
                                {user.role !== "student" && <Badge variant="secondary" className="text-xs py-0 h-4">{user.role}</Badge>}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-sm" data-testid={`text-user-email-${user.id}`}>{user.email || "–"}</td>
                            <td className="py-3 px-3 text-sm" data-testid={`text-user-grade-${user.id}`}>
                              {user.subscribedGrade ?? user.grade ?? "–"}
                            </td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-1.5">
                                {getStatusBadge(user.subscriptionStatus)}
                                {user.isTestAccount && <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 gap-1"><FlaskConical className="w-3 h-3" />Test</Badge>}
                              </div>
                            </td>
                            <td className="py-3 px-3"><LastActiveDot lastActiveAt={user.lastActiveAt} /></td>
                            <td className="py-3 px-3 text-sm text-muted-foreground" data-testid={`text-user-joined-${user.id}`}>
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", year: "numeric", month: "short", day: "numeric" }) : "–"}
                            </td>
                            <td className="py-3 px-3 text-sm" data-testid={`text-user-expiry-${user.id}`}>
                              {getTimeRemaining(user.subscriptionEndDate, user.subscriptionStatus)}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" data-testid={`button-actions-${user.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-52">
                                  <DropdownMenuItem onClick={() => setUserDetailsId(user.id)}>
                                    <Info className="w-4 h-4 mr-2" />View Details
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {user.isTestAccount ? (
                                    <DropdownMenuItem onClick={() => revokeTestAccessMutation.mutate(user.id)} disabled={revokeTestAccessMutation.isPending} className="text-destructive focus:text-destructive">
                                      <XIcon className="w-4 h-4 mr-2" />Delete Test Account
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => grantTestAccessMutation.mutate(user.id)} disabled={grantTestAccessMutation.isPending} className="text-blue-600">
                                      <FlaskConical className="w-4 h-4 mr-2" />Grant Test Access
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => setSelectedUserId(user.id)} data-testid={`menu-extend-${user.id}`}>
                                    <Plus className="w-4 h-4 mr-2" />Extend Subscription
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setApplyDiscountUserId(user.id); setCouponCode(""); }} data-testid={`menu-apply-discount-${user.id}`}>
                                    <Tag className="w-4 h-4 mr-2" />Apply Discount Code
                                  </DropdownMenuItem>
                                  {user.subscriptionStatus === "active" && (
                                    <DropdownMenuItem onClick={() => pauseMutation.mutate(user.id)} disabled={pauseMutation.isPending} data-testid={`menu-pause-${user.id}`}>
                                      <Pause className="w-4 h-4 mr-2" />Pause Subscription
                                    </DropdownMenuItem>
                                  )}
                                  {user.subscriptionStatus === "paused" && (
                                    <DropdownMenuItem onClick={() => resumeMutation.mutate(user.id)} disabled={resumeMutation.isPending} data-testid={`menu-resume-${user.id}`}>
                                      <Play className="w-4 h-4 mr-2" />Resume Subscription
                                    </DropdownMenuItem>
                                  )}
                                  {user.subscriptionStatus && user.subscriptionStatus !== "canceled" && (
                                    <DropdownMenuItem onClick={() => cancelMutation.mutate(user.id)} disabled={cancelMutation.isPending} data-testid={`menu-cancel-${user.id}`}>
                                      <X className="w-4 h-4 mr-2" />Cancel Subscription
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => setPasswordResetUserId(user.id)} data-testid={`menu-reset-password-${user.id}`}>
                                    <Key className="w-4 h-4 mr-2" />Reset Password
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => setDeleteUserId(user.id)} className="text-destructive focus:text-destructive" disabled={deleteMutation.isPending} data-testid={`menu-delete-${user.id}`}>
                                    <Trash2 className="w-4 h-4 mr-2" />Delete User
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>

                          {/* animated children row: always rendered for parent-role users */}
                          {isParent && (
                            <ChildrenRows
                              parentId={user.id}
                              isExpanded={isExpanded}
                              onViewDetails={setUserDetailsId}
                            />
                          )}
                        </tbody>
                      );
                    })}
                </table>
              </div>

              {/* Pagination */}
              {usersData && usersData.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((usersData.page - 1) * usersData.pageSize) + 1}–{Math.min(usersData.page * usersData.pageSize, usersData.total)} of {usersData.total}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || isFetching} data-testid="button-prev-page">
                      <ChevronLeft className="w-4 h-4 mr-1" />Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, usersData.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (usersData.totalPages <= 5) pageNum = i + 1;
                        else if (currentPage <= 3) pageNum = i + 1;
                        else if (currentPage >= usersData.totalPages - 2) pageNum = usersData.totalPages - 4 + i;
                        else pageNum = currentPage - 2 + i;
                        return (
                          <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="icon" onClick={() => setCurrentPage(pageNum)} disabled={isFetching} data-testid={`button-page-${pageNum}`}>
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(usersData.totalPages, p + 1))} disabled={currentPage === usersData.totalPages || isFetching} data-testid="button-next-page">
                      Next<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {usersData && usersData.users.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {debouncedSearch || statusFilter !== "all" || gradeFilter !== "all" || roleFilter !== "all"
                    ? "No users found matching your filters"
                    : "No users found"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── dialogs ─────────────────────────────────────────────────────── */}

        {/* Extend single */}
        <Dialog open={!!selectedUserId} onOpenChange={(open) => !open && setSelectedUserId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Extend Subscription</DialogTitle>
              <DialogDescription>
                Extend for {usersData?.users?.find(u => u.id === selectedUserId)?.firstName} {usersData?.users?.find(u => u.id === selectedUserId)?.lastName}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="days">Number of Days</Label>
              <Input id="days" type="number" min="1" value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)} className="mt-2" data-testid="input-extend-days" />
            </div>
            <DialogFooter>
              <Button onClick={() => { if (selectedUserId) extendMutation.mutate({ userId: selectedUserId, days: extendDays }); }} disabled={extendMutation.isPending} data-testid="button-confirm-extend">
                {extendMutation.isPending ? "Extending…" : "Confirm"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Apply Discount Code */}
        <Dialog open={!!applyDiscountUserId} onOpenChange={(open) => { if (!open) { setApplyDiscountUserId(null); setCouponCode(""); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Apply Discount Code</DialogTitle>
              <DialogDescription>
                Apply a Stripe coupon or promotion code to{" "}
                {usersData?.users?.find(u => u.id === applyDiscountUserId)?.firstName}{" "}
                {usersData?.users?.find(u => u.id === applyDiscountUserId)?.lastName}'s subscription.
                The discount will be applied to their next invoice.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <Label htmlFor="coupon-code">Coupon or Promotion Code</Label>
              <Input
                id="coupon-code"
                placeholder="e.g. WELCOME50 or coupon ID"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                className="mt-1"
                data-testid="input-coupon-code"
              />
              <p className="text-xs text-muted-foreground">
                Enter the promotion code you shared with the customer, or a Stripe coupon ID. The code is looked up in Stripe. Make sure it exists and is active.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setApplyDiscountUserId(null); setCouponCode(""); }}>
                Cancel
              </Button>
              <Button
                onClick={() => { if (applyDiscountUserId && couponCode.trim()) applyDiscountMutation.mutate({ userId: applyDiscountUserId, code: couponCode }); }}
                disabled={applyDiscountMutation.isPending || !couponCode.trim()}
                data-testid="button-confirm-apply-discount"
              >
                {applyDiscountMutation.isPending ? "Applying…" : "Apply Discount"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset */}
        <Dialog open={!!passwordResetUserId} onOpenChange={(open) => !open && setPasswordResetUserId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset User Password</DialogTitle>
              <DialogDescription>
                New password for {usersData?.users?.find(u => u.id === passwordResetUserId)?.firstName} {usersData?.users?.find(u => u.id === passwordResetUserId)?.lastName}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="new-password">New Password</Label>
              <Input id="new-password" type="password" placeholder="Min 6 characters" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-2" data-testid="input-new-password" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPasswordResetUserId(null); setNewPassword(""); }}>Cancel</Button>
              <Button onClick={() => { if (passwordResetUserId) resetPasswordMutation.mutate({ userId: passwordResetUserId, password: newPassword }); }} disabled={resetPasswordMutation.isPending || newPassword.length < 6} data-testid="button-confirm-password-reset">
                {resetPasswordMutation.isPending ? "Resetting…" : "Reset Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* WhatsApp */}
        <Dialog open={whatsappOpen} onOpenChange={(open) => { if (!open) { setWhatsappOpen(false); setWhatsappMessage(""); setWhatsappTargetId(null); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-500" />
                Send WhatsApp Message to Parent
              </DialogTitle>
              <DialogDescription>This message will be sent to the parent's registered WhatsApp number.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="wa-message">Message</Label>
              <Textarea id="wa-message" placeholder="Type your message…" value={whatsappMessage} onChange={(e) => setWhatsappMessage(e.target.value)} className="mt-2 min-h-[100px]" data-testid="input-whatsapp-message" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setWhatsappOpen(false); setWhatsappMessage(""); setWhatsappTargetId(null); }}>Cancel</Button>
              <Button onClick={() => { if (whatsappTargetId && whatsappMessage.trim()) whatsappMutation.mutate({ userId: whatsappTargetId, message: whatsappMessage }); }} disabled={whatsappMutation.isPending || !whatsappMessage.trim()} data-testid="button-send-whatsapp">
                {whatsappMutation.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send Message"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation: lives OUTSIDE the dropdown to avoid Radix scroll-lock conflicts */}
        <AlertDialog open={!!deleteUserId} onOpenChange={(open) => !open && setDeleteUserId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User?</AlertDialogTitle>
              <AlertDialogDescription>
                {(() => {
                  const u = usersData?.users.find(u => u.id === deleteUserId);
                  return u
                    ? `This will permanently delete ${u.firstName} ${u.lastName} and all their data. This cannot be undone.`
                    : "This will permanently delete this user and all their data. This cannot be undone.";
                })()}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => { if (deleteUserId) deleteMutation.mutate(deleteUserId); }}
                className="bg-destructive text-destructive-foreground"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete User"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* User Details */}
        <Dialog open={!!userDetailsId} onOpenChange={(open) => !open && handleCloseUserDetails()}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5" />User Details
              </DialogTitle>
              <DialogDescription>
                Comprehensive information for {userDetails?.firstName} {userDetails?.lastName}
              </DialogDescription>
            </DialogHeader>
            {userDetails && (
              <div className="space-y-6 py-4 overflow-y-auto flex-1">
                {/* Profile */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Username</Label>
                    {editingUsername ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input value={editedUsername} onChange={(e) => setEditedUsername(e.target.value)} placeholder="Username" className="flex-1" autoFocus data-testid="input-edit-username" />
                        <Button size="sm" onClick={() => { if (userDetailsId && editedUsername.length >= 3) updateUsernameMutation.mutate({ userId: userDetailsId, username: editedUsername }); }} disabled={updateUsernameMutation.isPending || editedUsername.length < 3} data-testid="button-save-username">
                          <Check className="w-4 h-4 mr-1" />Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditingUsername(false); setEditedUsername(userDetails.username); }} data-testid="button-cancel-username">
                          <XIcon className="w-4 h-4 mr-1" />Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="font-medium flex-1" data-testid="text-details-username">{getDisplayName(userDetails)}</p>
                        <Button size="sm" variant="outline" onClick={() => { setEditingUsername(true); setEditedUsername(userDetails.username); }} data-testid="button-edit-username">
                          <Edit2 className="w-4 h-4 mr-1" />Edit
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Full Name</Label>
                    <p className="font-medium" data-testid="text-details-fullname">{userDetails.firstName} {userDetails.lastName}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Email</Label>
                    {editingEmail ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input type="email" value={editedEmail} onChange={(e) => setEditedEmail(e.target.value)} placeholder="Email" className="flex-1" autoFocus data-testid="input-edit-email" />
                        <Button size="sm" onClick={() => { if (userDetailsId && editedEmail.includes('@')) updateEmailMutation.mutate({ userId: userDetailsId, email: editedEmail }); }} disabled={updateEmailMutation.isPending || !editedEmail.includes('@')} data-testid="button-save-email">
                          <Check className="w-4 h-4 mr-1" />Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditingEmail(false); setEditedEmail(userDetails.email || ""); }} data-testid="button-cancel-email">
                          <XIcon className="w-4 h-4 mr-1" />Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="font-medium flex-1" data-testid="text-details-email">{userDetails.email || "N/A"}</p>
                        <Button size="sm" variant="outline" onClick={() => { setEditingEmail(true); setEditedEmail(userDetails.email || ""); }} data-testid="button-edit-email">
                          <Edit2 className="w-4 h-4 mr-1" />Edit
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">School</Label>
                    <p className="font-medium" data-testid="text-details-school">{userDetails.school || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Grade</Label>
                    <Select value={userDetails.grade?.toString()} onValueChange={(v) => { if (userDetailsId) updateGradeMutation.mutate({ userId: userDetailsId, grade: parseInt(v) }); }} disabled={updateGradeMutation.isPending}>
                      <SelectTrigger className="w-full" data-testid="select-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">Grade 4</SelectItem>
                        <SelectItem value="5">Grade 5</SelectItem>
                        <SelectItem value="6">Grade 6</SelectItem>
                        <SelectItem value="11">Grade 11</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Gender</Label>
                    <p className="font-medium" data-testid="text-details-gender">{userDetails.gender === "male" ? "Boy" : userDetails.gender === "female" ? "Girl" : userDetails.gender || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Date of Birth</Label>
                    <p className="font-medium" data-testid="text-details-dob">{userDetails.dateOfBirth || "N/A"}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Role</Label>
                    <Badge variant="outline" data-testid="badge-details-role">{userDetails.role}</Badge>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Last Active</Label>
                    <div className="mt-1"><LastActiveDot lastActiveAt={userDetails.lastActiveAt} /></div>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Account Created</Label>
                    <p className="font-medium" data-testid="text-details-created-at">
                      {userDetails.createdAt ? new Date(userDetails.createdAt).toLocaleDateString("en-JM", { timeZone: "America/Jamaica", year: "numeric", month: "long", day: "numeric" }) : "N/A"}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Level */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className="w-4 h-4" />Level & Progress</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div><Label className="text-sm text-muted-foreground">Current Level</Label><p className="font-medium text-lg" data-testid="text-details-level">{userDetails.level?.currentLevel ?? 0}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Total XP</Label><p className="font-medium" data-testid="text-details-total-xp">{userDetails.level?.totalXP ?? 0}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Level XP</Label><p className="font-medium" data-testid="text-details-level-xp">{userDetails.level?.levelXP ?? 0}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Next Level</Label><p className="font-medium" data-testid="text-details-next-level-xp">{userDetails.level?.nextLevelXP ?? 0} XP</p></div>
                  </div>
                </div>

                <Separator />

                {/* Stats */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Performance Stats</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                    <div><Label className="text-sm text-muted-foreground">Questions Attempted</Label><p className="font-medium text-lg" data-testid="text-details-total-tests">{userDetails.stats?.totalTests || 0}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Correct</Label><p className="font-medium text-lg" data-testid="text-details-total-correct">{userDetails.stats?.totalCorrect || 0}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Overall Accuracy</Label><p className="font-medium text-lg" data-testid="text-details-accuracy">{userDetails.stats?.overallAccuracy ? `${Math.round(userDetails.stats.overallAccuracy)}%` : "N/A"}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Total Points</Label><p className="font-medium text-lg" data-testid="text-details-total-points">{userDetails.stats?.totalPoints || 0}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Current Streak</Label><p className="font-medium text-lg" data-testid="text-details-streak">{userDetails.stats?.currentStreak || 0}</p></div>
                  </div>
                  {(userDetails.stats?.subjectBreakdown?.length ?? 0) > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {userDetails.stats!.subjectBreakdown!.map(s => (
                        <div key={s.subject} className="rounded-md bg-muted/50 px-3 py-2">
                          <Label className="text-xs text-muted-foreground">{s.subject}</Label>
                          <p className="font-medium text-sm mt-0.5">{s.points} pts</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Parent/Guardian */}
                <>
                  <Separator />
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" />Parent / Guardian</h3>
                      {!editingParentContact ? (
                        <Button size="sm" variant="outline"
                          onClick={() => {
                            setEditedParentFirstName(userDetails.parentFirstName || "");
                            setEditedParentLastName(userDetails.parentLastName || "");
                            setEditedParentEmail(userDetails.parentEmail || "");
                            setEditedParentPhone(userDetails.parentPhone || "");
                            setEditingParentContact(true);
                          }}
                          data-testid="button-edit-parent-contact">
                          <Edit2 className="w-4 h-4 mr-1" />Edit
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button size="sm"
                            onClick={() => {
                              if (userDetailsId) updateParentContactMutation.mutate({
                                userId: userDetailsId,
                                parentFirstName: editedParentFirstName,
                                parentLastName: editedParentLastName,
                                parentEmail: editedParentEmail,
                                parentPhone: editedParentPhone,
                              });
                            }}
                            disabled={updateParentContactMutation.isPending}
                            data-testid="button-save-parent-contact">
                            <Check className="w-4 h-4 mr-1" />Save
                          </Button>
                          <Button size="sm" variant="outline"
                            onClick={() => setEditingParentContact(false)}
                            data-testid="button-cancel-parent-contact">
                            <XIcon className="w-4 h-4 mr-1" />Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                    {editingParentContact ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-sm text-muted-foreground">First Name</Label>
                          <Input value={editedParentFirstName} onChange={(e) => setEditedParentFirstName(e.target.value)} placeholder="First name" data-testid="input-parent-first-name" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-muted-foreground">Last Name</Label>
                          <Input value={editedParentLastName} onChange={(e) => setEditedParentLastName(e.target.value)} placeholder="Last name" data-testid="input-parent-last-name" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-muted-foreground">Email</Label>
                          <Input type="email" value={editedParentEmail} onChange={(e) => setEditedParentEmail(e.target.value)} placeholder="Email" data-testid="input-parent-email" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-sm text-muted-foreground">Phone</Label>
                          <Input type="tel" value={editedParentPhone} onChange={(e) => setEditedParentPhone(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 18763970572" data-testid="input-parent-phone" />
                          <p className="text-xs text-muted-foreground">Digits only, e.g. 18763970572</p>
                        </div>
                      </div>
                    ) : (userDetails.parentFirstName || userDetails.parentName || userDetails.parentEmail || userDetails.parentPhone) ? (
                      <div className="grid grid-cols-2 gap-4">
                        {(userDetails.parentFirstName || userDetails.parentName) && (
                          <div>
                            <Label className="text-sm text-muted-foreground">Name</Label>
                            <p className="font-medium" data-testid="text-details-parent-name">
                              {userDetails.parentFirstName ? `${userDetails.parentFirstName} ${userDetails.parentLastName || ""}`.trim() : userDetails.parentName}
                            </p>
                          </div>
                        )}
                        {userDetails.parentEmail && <div><Label className="text-sm text-muted-foreground">Email</Label><p className="font-medium" data-testid="text-details-parent-email">{userDetails.parentEmail}</p></div>}
                        {userDetails.parentPhone && <div><Label className="text-sm text-muted-foreground">Phone</Label><p className="font-medium font-mono" data-testid="text-details-parent-phone">{formatPhoneDisplay(userDetails.parentPhone)}</p></div>}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No parent contact info on file.</p>
                    )}
                    {!editingParentContact && userDetails.parentPhone && (
                      <div className="mt-3">
                        <Button size="sm" variant="outline" className="border-green-500 text-green-600 dark:text-green-400"
                          onClick={() => { setWhatsappTargetId(userDetailsId); setWhatsappOpen(true); }}
                          data-testid="button-whatsapp-parent">
                          <MessageSquare className="w-4 h-4 mr-2" />Message via WhatsApp
                        </Button>
                      </div>
                    )}
                  </div>
                </>

                {/* Referral */}
                {userDetails.referralInfo && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2"><Link2 className="w-4 h-4" />Referral Partner</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label className="text-sm text-muted-foreground">Referral Code</Label><p className="font-medium font-mono" data-testid="text-details-referral-code">{userDetails.referralInfo.referralCode}</p></div>
                        <div><Label className="text-sm text-muted-foreground">Program Type</Label><Badge variant="outline" className="capitalize">{userDetails.referralInfo.programType}</Badge></div>
                        <div><Label className="text-sm text-muted-foreground">Total Referrals</Label><p className="font-medium text-lg" data-testid="text-details-total-referrals">{userDetails.referralInfo.totalReferrals}</p></div>
                        <div><Label className="text-sm text-muted-foreground">Active Referrals</Label><p className="font-medium text-lg text-green-600 dark:text-green-400" data-testid="text-details-active-referrals">{userDetails.referralInfo.activeReferrals}</p></div>
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                {/* Subscription */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" />Subscription Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-sm text-muted-foreground">Status</Label><div className="mt-1">{getStatusBadge(userDetails.subscriptionStatus)}</div></div>
                    <div><Label className="text-sm text-muted-foreground">Plan</Label><p className="font-medium" data-testid="text-details-plan">{userDetails.subscriptionPlan || "N/A"}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Subscribed Grade</Label><p className="font-medium" data-testid="text-details-subscribed-grade">{userDetails.subscribedGrade ?? "N/A"}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Expires</Label><p className="font-medium" data-testid="text-details-expiry">{getTimeRemaining(userDetails.subscriptionEndDate, userDetails.subscriptionStatus)}</p></div>
                  </div>
                </div>

                <Separator />

                {/* Payment History */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" />Payment History</h3>
                  {paymentHistoryLoading ? (
                    <div className="flex items-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /><span className="ml-2 text-sm text-muted-foreground">Loading…</span></div>
                  ) : paymentHistory?.message ? (
                    <p className="text-sm text-muted-foreground py-2">{paymentHistory.message}</p>
                  ) : (
                    <div className="space-y-4">
                      {paymentHistory?.upcomingPayments && paymentHistory.upcomingPayments.length > 0 && (
                        <div>
                          <Label className="text-sm text-muted-foreground mb-2 block">Upcoming Payment</Label>
                          {paymentHistory.upcomingPayments.map((p: any, i: number) => p && (
                            <div key={i} className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
                              <div>
                                <p className="font-medium text-blue-700 dark:text-blue-300">{p.currency || 'USD'} ${(p.amount ?? 0).toFixed(2)}</p>
                                <p className="text-sm text-blue-600 dark:text-blue-400">Due: {p.date ? formatDate(p.date) : 'Unknown'}</p>
                              </div>
                              <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">Scheduled</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                      {paymentHistory?.pastPayments && paymentHistory.pastPayments.length > 0 ? (
                        <div>
                          <Label className="text-sm text-muted-foreground mb-2 block">Past Payments</Label>
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {paymentHistory.pastPayments.map((p: any) => p && (
                              <div key={p.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-md" data-testid={`payment-${p.id}`}>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium">{p.currency || 'USD'} ${(p.amount ?? 0).toFixed(2)}</p>
                                    <Badge variant={p.status === 'paid' ? 'default' : 'secondary'} className={p.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : p.status === 'open' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' : ''}>
                                      {p.status || 'unknown'}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{p.date ? formatDate(p.date) : 'Unknown'}, {p.description || 'Payment'}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {p.invoiceUrl && <a href={p.invoiceUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`link-invoice-${p.id}`}><ExternalLink className="w-4 h-4" /></a>}
                                  {p.invoicePdf && <a href={p.invoicePdf} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`link-pdf-${p.id}`}><FileText className="w-4 h-4" /></a>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : <p className="text-sm text-muted-foreground py-2">No past payments found</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
